<script setup>
import { ref, computed, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { useInstantBookingSlotsStore, useUsersStore, useRestaurantsStore } from '@/stores'
import moment from 'moment-timezone'
import PreferredIcon from '@core/components/PreferredIcon.vue'
import { generateGeneralReservationUrlWithQueryString } from '@/plugins/misc'
import { sanitizeHtml } from '@core/utils/sanitizeHtml'

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  slotId: {
    type: String,
    default: null
  }
})

const emit = defineEmits(['update:modelValue'])

const instantBookingSlotsStore = useInstantBookingSlotsStore()
const usersStore = useUsersStore()
const restaurantsStore = useRestaurantsStore()

const { myUser } = storeToRefs(usersStore)
const { currentSlot, loading } = storeToRefs(instantBookingSlotsStore)
const { viewingDetailsRestaurant } = storeToRefs(restaurantsStore)

// Get restaurant data
const restaurant = computed(() => {
  if (!currentSlot.value) return null

  return restaurantsStore.restaurants.find(r => r._id === currentSlot.value.restaurantId) || {
    name: currentSlot.value.restaurantName,
    market: currentSlot.value.restaurantMarket
  }
})

// Close dialog handler
const closeDialog = () => {
  emit('update:modelValue', false)
}

// Open restaurant details dialog
const openRestaurantDetails = () => {
  if (restaurant.value) {
    viewingDetailsRestaurant.value = restaurant.value
  }
}

// Check if restaurant is Preferred
const isPreferred = computed(() => {
  return restaurant.value?.status === 'Preferred'
})

// General availability URL
const generalAvailabilityUrl = computed(() => {
  if (!currentSlot.value || !restaurant.value?.generalReservationUrl) return null
  if (currentSlot.value.showGeneralAvailability === false) return null

  const tz = currentSlot.value.restaurantTimeZone || 'America/New_York'

  const reservationData = {
    date: moment.tz(currentSlot.value.date, tz).format('YYYY-MM-DD'),
    preferredTime: formattedTime.value.toLowerCase().replace(' ', ''),
    partySize: currentSlot.value.partySize
  }

  return generateGeneralReservationUrlWithQueryString(restaurant.value, reservationData)
})

const generalReservationPlatform = computed(() => {
  return restaurant.value?.generalReservationPlatform || 'restaurant website'
})

// Format date and time
const formattedDate = computed(() => {
  if (!currentSlot.value) return ''
  const tz = currentSlot.value.restaurantTimeZone || 'America/New_York'

  return moment.tz(currentSlot.value.date, tz).format('dddd, MMMM D, YYYY')
})

const formattedTime = computed(() => {
  if (!currentSlot.value?.time) return ''
  const time = currentSlot.value.time
  const parsed = moment(time, ['HH:mm', 'H:mm', 'h:mm A', 'h:mm a'])
  if (parsed.isValid()) {
    return parsed.format('h:mm A')
  }

  return time
})

// Format currency with half-up rounding to 2 decimal places
const formatCurrency = amount => {
  const rounded = Math.round((amount + Number.EPSILON) * 100) / 100

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(rounded)
}

// Pricing breakdown (optional for external slots)
const pricingBreakdown = computed(() => {
  if (!currentSlot.value || !currentSlot.value.guaranteedSpendPerPerson) return null

  const slot = currentSlot.value
  const baseSpend = slot.guaranteedSpendPerPerson * slot.partySize

  // Only calculate tax/tip/cc if they exist
  const tax = slot.taxPercentage ? baseSpend * (slot.taxPercentage / 100) : 0
  const tip = slot.tipPercentage ? baseSpend * (slot.tipPercentage / 100) : 0
  const ccFee = slot.ccFeePercentage ? (baseSpend + tax + tip) * (slot.ccFeePercentage / 100) : 0
  const total = baseSpend + tax + tip + ccFee

  return {
    baseSpend,
    tax,
    taxPercentage: slot.taxPercentage || 0,
    tip,
    tipPercentage: slot.tipPercentage || 0,
    ccFee,
    ccFeePercentage: slot.ccFeePercentage || 0,
    total
  }
})

// Experience description truncation
const descriptionExpanded = ref(false)
const descriptionCharLimit = 280

const stripHtml = html => {
  const div = document.createElement('div')

  // Sanitize before touching innerHTML: assigning untrusted HTML here would let
  // e.g. `<img src=x onerror=...>` fire even on this detached node.
  div.innerHTML = sanitizeHtml(html)

  return div.textContent || div.innerText || ''
}

const descriptionPlainText = computed(() => {
  if (!currentSlot.value?.experienceDescription) return ''
  
  return stripHtml(currentSlot.value.experienceDescription)
})

const isDescriptionLong = computed(() => {
  return descriptionPlainText.value.length > descriptionCharLimit
})

const truncatedDescription = computed(() => {
  if (!isDescriptionLong.value) return descriptionPlainText.value
  
  return descriptionPlainText.value.slice(0, descriptionCharLimit).trimEnd() + '...'
})

// Open external booking URL
const handleBookNow = () => {
  if (currentSlot.value?.externalBookingUrl) {
    window.open(currentSlot.value.externalBookingUrl, '_blank')
  }
}

// Share restaurant page
const shareUrl = computed(() => {
  if (!restaurant.value?.profileId) return null
  
  return `${window.location.origin}/r/${restaurant.value.profileId}`
})

const copied = ref(false)

const handleShare = async () => {
  if (!shareUrl.value) return

  if (navigator.share) {
    try {
      await navigator.share({
        title: currentSlot.value?.restaurantName || 'Restaurant',
        url: shareUrl.value
      })
      
      return
    } catch (e) {
      if (e.name === 'AbortError') return
    }
  }

  try {
    await navigator.clipboard.writeText(shareUrl.value)
    copied.value = true
    setTimeout(() => { copied.value = false }, 2000)
  } catch (e) {
    console.error('Failed to copy:', e)
  }
}

// Initialize when dialog opens
const initializeDialog = async () => {
  if (!props.slotId) return

  await instantBookingSlotsStore.fetchSlotById(props.slotId)

  // Use populated restaurant data from slot response (works without auth)
  if (currentSlot.value?.restaurant) {
    const existing = restaurantsStore.restaurants.find(r => r._id === currentSlot.value.restaurant._id)
    if (existing) Object.assign(existing, currentSlot.value.restaurant)
    else restaurantsStore.restaurants.push(currentSlot.value.restaurant)
  }

  // Fetch full restaurant data if logged in (may include additional fields)
  if (currentSlot.value?.restaurantId) {
    await restaurantsStore.getRestaurantById(currentSlot.value.restaurantId)
  }
}

// Watch for dialog open/close
watch(() => props.modelValue, async isOpen => {
  if (isOpen && props.slotId) {
    descriptionExpanded.value = false
    await initializeDialog()
  }
})
</script>

<template>
  <VDialog
    :model-value="modelValue"
    fullscreen
    :scrim="false"
    transition="dialog-bottom-transition"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <VCard class="external-booking-dialog">
      <!-- Close Button -->
      <VBtn
        icon
        variant="text"
        class="close-btn"
        @click="closeDialog"
      >
        <VIcon
          icon="mdi-close"
          size="24"
        />
      </VBtn>

      <div class="dialog-content">
        <!-- Loading State -->
        <div
          v-if="loading"
          class="loading-state"
        >
          <VProgressCircular
            indeterminate
            color="primary"
            size="64"
          />
          <p class="text-body-1 text-medium-emphasis mt-4">
            Loading booking details...
          </p>
        </div>

        <!-- Content -->
        <div
          v-else-if="currentSlot"
          class="checkout-content"
        >
          <!-- Back Navigation -->
          <button
            class="back-button"
            @click="closeDialog"
          >
            <VIcon
              icon="mdi-arrow-left"
              size="18"
            />
            <span>Back</span>
          </button>

          <div class="checkout-columns">
            <!-- Left Column - Restaurant & Details -->
            <div class="checkout-left">
              <!-- Restaurant Header Card -->
              <div class="restaurant-card">
                <div class="restaurant-header">
                  <PreferredIcon
                    v-if="isPreferred"
                    :size="24"
                    class="preferred-icon"
                  />
                  <h1
                    class="restaurant-name restaurant-name-clickable"
                    @click="openRestaurantDetails"
                  >
                    {{ currentSlot.restaurantName }}
                  </h1>
                  <VBtn
                    v-if="shareUrl"
                    icon
                    variant="text"
                    size="x-small"
                    :title="copied ? 'Link copied!' : 'Share restaurant'"
                    @click="handleShare"
                  >
                    <VIcon
                      :icon="copied ? 'mdi-check' : 'ion-share-outline'"
                      size="18"
                      :color="copied ? 'primary-dark' : undefined"
                    />
                  </VBtn>
                </div>
                <p class="restaurant-address">
                  {{ restaurant?.address || currentSlot.restaurantMarket }}
                </p>

                <div
                  v-if="currentSlot.experienceName"
                  class="experience-badge"
                >
                  {{ currentSlot.experienceName }}
                </div>

                <div
                  v-if="currentSlot.experienceDescription && (descriptionExpanded || !isDescriptionLong)"
                  class="experience-description"
                  v-html="sanitizeHtml(currentSlot.experienceDescription)"
                />
                <div
                  v-else-if="currentSlot.experienceDescription && isDescriptionLong"
                  class="experience-description"
                >
                  {{ truncatedDescription }}
                </div>
                <button
                  v-if="isDescriptionLong"
                  class="read-more-btn"
                  @click="descriptionExpanded = !descriptionExpanded"
                >
                  {{ descriptionExpanded ? 'Read less' : 'Read more' }}
                </button>
              </div>

              <!-- Experience Details Card -->
              <div
                v-if="pricingBreakdown"
                class="details-card"
              >
                <h2 class="details-title">
                  Experience Details
                </h2>
                <div class="details-list">
                  <p class="detail-item">
                    {{ formattedDate }}
                  </p>
                  <p class="detail-item">
                    {{ formattedTime }}
                  </p>
                  <p class="detail-item">
                    Party of {{ currentSlot.partySize }}
                  </p>
                  <p
                    v-if="currentSlot.seatingArea"
                    class="detail-item"
                  >
                    {{ currentSlot.seatingArea }}
                  </p>
                  <p class="detail-item detail-bold">
                    Subtotal: {{ formatCurrency(pricingBreakdown.baseSpend) }} ({{
                      formatCurrency(currentSlot.guaranteedSpendPerPerson) }}pp)
                  </p>
                  <p class="detail-item detail-light">
                    Tax ({{ pricingBreakdown.taxPercentage }}%): {{ formatCurrency(pricingBreakdown.tax) }}<template v-if="pricingBreakdown.tipPercentage > 0">
                      | Gratuity ({{
                        pricingBreakdown.tipPercentage }}%): {{ formatCurrency(pricingBreakdown.tip) }}
                    </template>
                  </p>
                  <p
                    v-if="pricingBreakdown.ccFee > 0"
                    class="detail-item detail-light"
                  >
                    CC Fee ({{ pricingBreakdown.ccFeePercentage }}%): {{ formatCurrency(pricingBreakdown.ccFee) }}
                  </p>
                  <p class="detail-item detail-bold">
                    Total: {{ formatCurrency(pricingBreakdown.total) }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Right Column - Book Now -->
            <div class="checkout-right">
              <!-- Book Now Button -->
              <VBtn
                color="primary-dark"
                size="x-large"
                block
                class="book-button"
                @click="handleBookNow"
              >
                Book Now
                <VIcon
                  icon="mdi-open-in-new"
                  size="18"
                  class="ml-2"
                />
              </VBtn>
              <!-- Check General Availability Link -->
              <a
                v-if="generalAvailabilityUrl"
                :href="generalAvailabilityUrl"
                target="_blank"
                class="general-availability-link text-center justify-center"
              >
                check general availability ({{ generalReservationPlatform }})
                <VIcon
                  size="14"
                  class="ml-1"
                  icon="mdi-open-in-new"
                />
              </a>
              <!-- Questions -->
              <div class="questions-section">
                <p>questions?</p>
                <p>
                  email <a
                    href="mailto:preferred@reserverm.com"
                    class="email-link"
                  >preferred@reserverm.com</a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </VCard>
  </VDialog>
</template>

<style lang="scss" scoped>
$primary-teal: var(--ds-brand-primary-dark);
$text-dark: var(--ds-text-dark);
$text-medium: var(--ds-text-medium);
$text-light: var(--ds-text-light);
$border-light: var(--ds-border-light);
$bg-white: var(--ds-surface-card);
$bg-light: var(--ds-bg-light);

.external-booking-dialog {
  background-color: $bg-light;
  position: relative;
}

.close-btn {
  position: fixed;
  top: var(--ds-space-4);
  right: var(--ds-space-4);
  z-index: var(--ds-z-dropdown);
  background: $bg-white !important;
  box-shadow: var(--ds-shadow-sm);
}

.dialog-content {
  min-height: 100vh;
  padding: var(--ds-space-6) var(--ds-space-4);
  padding-top: 60px;
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
}

.checkout-content {
  max-width: 1100px;
  margin: 0 auto;
}

.back-button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: var(--ds-space-2) var(--ds-space-4) var(--ds-space-2) 10px;
  margin-bottom: var(--ds-space-4);
  background: $bg-white;
  border: 1px solid $border-light;
  border-radius: var(--ds-radius-sm);
  color: $text-medium;
  font-size: var(--ds-text-base);
  font-weight: 500;
  cursor: pointer;
  transition: all var(--ds-duration-normal) ease;

  &:hover {
    background: $bg-light;
    border-color: $text-light;
    color: $text-dark;
  }

  &:active {
    transform: translateX(-2px);
  }
}

.checkout-columns {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--ds-space-6);
  align-items: start;
  max-width: 700px;
}

// Left Column
.checkout-left {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-4);
}

.restaurant-card {
  background: $bg-white;
  border-radius: var(--ds-radius-md);
  padding: var(--ds-space-6);
  border: 1px solid $border-light;
}

.restaurant-header {
  display: flex;
  align-items: center;
  gap: var(--ds-space-2);
  margin-bottom: var(--ds-space-1);
}

.preferred-icon {
  flex-shrink: 0;
}

.restaurant-name {
  font-size: 1.75rem;
  font-weight: 600;
  color: $primary-teal;
  margin: 0;
  line-height: var(--ds-leading-tight);
}

.restaurant-name-clickable {
  cursor: pointer;
  transition: opacity var(--ds-duration-normal) ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
}

.restaurant-address {
  font-size: 0.95rem;
  color: $text-medium;
  margin: 0 0 var(--ds-space-4) 0;
}

.experience-badge {
  display: inline-block;
  padding: var(--ds-space-2) var(--ds-space-4);
  border: 1.5px solid $primary-teal;
  border-radius: var(--ds-radius-xl);
  color: $primary-teal;
  font-size: 0.85rem;
  font-weight: 500;
  margin-bottom: var(--ds-space-4);
}

.experience-description {
  font-size: 0.95rem;
  color: $text-dark;
  line-height: var(--ds-leading-normal);
  margin: 0;
}

.read-more-btn {
  background: none;
  border: none;
  padding: 0;
  margin-top: 6px;
  color: $primary-teal;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: opacity var(--ds-duration-normal) ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
}

.details-card {
  background: $bg-white;
  border-radius: var(--ds-radius-md);
  padding: var(--ds-space-6);
  border: 1px solid $border-light;
}

.details-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: $text-dark;
  margin: 0 0 var(--ds-space-4) 0;
}

.details-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.detail-item {
  font-size: 0.95rem;
  color: $text-dark;
  margin: 0;
  line-height: var(--ds-leading-normal);
}

.detail-bold {
  font-weight: 600;
}

.detail-light {
  color: $text-light;
  font-size: 0.9rem;
}

.general-availability-link {
  display: inline-flex;
  align-items: center;
  margin-top: var(--ds-space-4);
  font-size: 0.9rem;
  color: $primary-teal;
  text-decoration: none;
  transition: opacity var(--ds-duration-normal) ease;

  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
}

// Right Column
.checkout-right {
  display: flex;
  flex-direction: column;
  gap: var(--ds-space-4);
}

.book-button {
  border-radius: var(--ds-radius-md) !important;
  text-transform: none !important;
  font-size: 1rem !important;
  font-weight: 600 !important;
  letter-spacing: 0 !important;
  height: 56px !important;
}

.questions-section {
  text-align: center;
  padding: var(--ds-space-4) 0;

  p {
    margin: 0;
    font-size: 0.9rem;
    color: $text-medium;
    line-height: 1.6;
  }
}

.email-link {
  color: $primary-teal;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

// Mobile responsiveness
@media (max-width: 900px) {
  .dialog-content {
    padding: var(--ds-space-4) var(--ds-space-3);
    padding-top: 60px;
  }

  .restaurant-name {
    font-size: 1.5rem;
  }
}
</style>
